import { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { client } from '../../api/client';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ConfirmDialog, Toast } from '../ui/Dialog';
import type { ConfirmOptions } from '../ui/Dialog';
import { Plus, Pencil, Trash2, Calendar, X, Users, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { AttendanceDetail } from './AttendanceDetail';
import { getApiErrorMessage } from '../../utils/apiErrors';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OrchestraGroup {
  id: string;
  name: string;
}

interface Event {
  id: string;
  title: string;
  type: string;
  date: string;
  startTime: string;
  endTime: string | null;
  location: string | null;
  description: string | null;
  color: string | null;
  status: string;
  groups: OrchestraGroup[];
  template: { id: string; title: string } | null;
}

interface EventTemplate {
  id: string;
  title: string;
  type: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string | null;
  location: string | null;
  description: string | null;
  color: string | null;
  groups: OrchestraGroup[];
}

type EventFormData = {
  title: string;
  type: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  description: string;
  color: string;
  groups: string[];
};

type TemplateFormData = {
  title: string;
  type: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  location: string;
  description: string;
  color: string;
  groups: string[];
};

// ── Constants ─────────────────────────────────────────────────────────────────

const EVENT_TYPES = ['rehearsal', 'concert', 'uitstap'] as const;
const TYPE_LABELS: Record<string, string> = {
  rehearsal: 'Repetitie',
  concert: 'Concert',
  uitstap: 'Uitstap',
};
const DAY_NAMES = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];

const PRESET_COLORS = [
  '#0f3460', '#5b21b6', '#166534', '#9a3412', '#92400e',
  '#1e40af', '#be185d', '#374151', '#0e7490', '#7c3aed',
];

const EMPTY_EVENT_FORM: EventFormData = {
  title: '',
  type: 'rehearsal',
  date: '',
  startTime: '',
  endTime: '',
  location: '',
  description: '',
  color: '',
  groups: [],
};

const EMPTY_TEMPLATE_FORM: TemplateFormData = {
  title: '',
  type: 'rehearsal',
  dayOfWeek: '5',
  startTime: '',
  endTime: '',
  location: '',
  description: '',
  color: '',
  groups: [],
};

// ── Styled components ─────────────────────────────────────────────────────────

const SubTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const SubTab = styled.button<{ $active: boolean }>`
  background: none;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary.royalBlue : '#ddd')};
  border-radius: 4px;
  padding: 0.4rem 1rem;
  font-size: 0.9rem;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary.royalBlue : '#666')};
  background-color: ${({ $active }) => ($active ? '#f0f9ff' : 'transparent')};
  cursor: pointer;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  font-size: 0.875rem;
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
  font-size: 0.9rem;
`;

const Badge = styled.span<{ $color: 'blue' | 'purple' | 'green' | 'orange' | 'red' | 'gray' }>`
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-right: 0.25rem;
  background-color: ${({ $color }) =>
    $color === 'blue' ? '#dbeafe'
    : $color === 'purple' ? '#ede9fe'
    : $color === 'green' ? '#dcfce7'
    : $color === 'orange' ? '#ffedd5'
    : $color === 'red' ? '#fee2e2'
    : '#f3f4f6'};
  color: ${({ $color }) =>
    $color === 'blue' ? '#1e40af'
    : $color === 'purple' ? '#5b21b6'
    : $color === 'green' ? '#166534'
    : $color === 'orange' ? '#9a3412'
    : $color === 'red' ? '#991b1b'
    : '#374151'};
`;

const ActionBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 0.25rem;
  margin-right: 0.25rem;
  border-radius: 4px;
  &:hover { color: ${({ theme }) => theme.colors.primary.royalBlue}; background: #f0f9ff; }
`;

const DeleteBtn = styled(ActionBtn)`
  &:hover { color: #dc2626; background: #fee2e2; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 5vh;
  z-index: ${({ theme }) => theme.zIndices.modalBackdrop};
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  width: 100%;
  max-width: 540px;
  max-height: 88vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const FieldLabel = styled.label`
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.3rem;
  color: #374151;
`;

const FieldGroup = styled.div`
  margin-bottom: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95rem;
  box-sizing: border-box;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95rem;
  resize: vertical;
  box-sizing: border-box;
`;

const ColorSwatches = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  align-items: center;
`;

const ColorSwatch = styled.button<{ $color: string; $active: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 2.5px solid ${({ $active }) => ($active ? '#1a1a2e' : 'transparent')};
  outline: ${({ $active }) => ($active ? '2px solid #fff' : 'none')};
  outline-offset: -4px;
  cursor: pointer;
  padding: 0;
  transition: transform 0.1s;
  &:hover { transform: scale(1.2); }
`;

const ColorInput = styled.input`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #d1d5db;
  padding: 0;
  cursor: pointer;
  background: none;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const GenerateBox = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem 1.25rem;
  margin-top: 0.5rem;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: center;
  margin-bottom: 1rem;
`;

const SearchLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.38rem 0.6rem;
  background: white;
  color: #6b7280;
  flex: 1;
  min-width: 200px;

  input {
    border: 0;
    outline: 0;
    font-size: 0.85rem;
    color: #111827;
    background: transparent;
    width: 100%;
  }
`;

const FilterSelect = styled.select`
  padding: 0.38rem 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.85rem;
  background: white;
  color: #374151;
  cursor: pointer;
`;

const SectionTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1.25rem 0 0.5rem;
  cursor: pointer;
  user-select: none;
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function typeColor(type: string): 'blue' | 'purple' | 'orange' {
  if (type === 'concert') return 'purple';
  if (type === 'uitstap') return 'orange';
  return 'blue';
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('nl-BE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function toDateInputValue(iso: string) {
  return iso.slice(0, 10);
}

function endTimeFromDescription(description: string | null, startTime: string) {
  if (!description) return null;
  const times = [...description.matchAll(/(\d{1,2})[:.](\d{2})/g)]
    .map((match) => `${(match[1] ?? '').padStart(2, '0')}:${match[2] ?? ''}`);
  if (times.length < 2) return null;
  const startIndex = times.findIndex((time) => time === startTime);
  return startIndex >= 0 ? times[startIndex + 1] ?? null : times[1] ?? null;
}

function displayEndTime(item: { endTime: string | null; description: string | null; startTime: string }) {
  return item.endTime || endTimeFromDescription(item.description, item.startTime) || '—';
}

// ── Color picker helper ───────────────────────────────────────────────────────

const ColorPickerField = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (color: string) => void;
}) => (
  <ColorSwatches>
    {PRESET_COLORS.map((c) => (
      <ColorSwatch key={c} $color={c} $active={value === c} onClick={() => onChange(value === c ? '' : c)} type="button" title={c} />
    ))}
    <ColorInput
      type="color"
      value={value || '#0f3460'}
      title="Aangepaste kleur"
      onChange={(e) => onChange(e.target.value)}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        style={{ fontSize: '0.75rem', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        Wis
      </button>
    )}
  </ColorSwatches>
);

// ── Group selector helper ─────────────────────────────────────────────────────

const GroupCheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const GroupCheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  cursor: pointer;
`;

const GroupSelect = ({
  groups,
  selected,
  onChange,
}: {
  groups: OrchestraGroup[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) => (
  <GroupCheckboxList>
    {groups.map((g) => (
      <GroupCheckboxRow key={g.id}>
        <input
          type="checkbox"
          checked={selected.includes(g.id)}
          onChange={(e) => onChange(e.target.checked ? [...selected, g.id] : selected.filter((id) => id !== g.id))}
        />
        {g.name}
      </GroupCheckboxRow>
    ))}
  </GroupCheckboxList>
);

// ── Event form modal ──────────────────────────────────────────────────────────

const EventModal = ({
  initial,
  groups,
  onSave,
  onClose,
}: {
  initial: EventFormData & { id?: string };
  groups: OrchestraGroup[];
  onSave: (data: EventFormData & { id?: string }) => Promise<void>;
  onClose: () => void;
}) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const set = (key: keyof EventFormData, val: string | string[]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.title || !form.date || !form.startTime || form.groups.length === 0) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3>{form.id ? 'Evenement bewerken' : 'Nieuw evenement'}</h3>
          <ActionBtn onClick={onClose}><X size={18} /></ActionBtn>
        </ModalHeader>

        <FieldGroup>
          <FieldLabel>Titel *</FieldLabel>
          <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="bv. Repetitie Harmonie" />
        </FieldGroup>

        <Row>
          <FieldGroup>
            <FieldLabel>Type *</FieldLabel>
            <Select value={form.type} onChange={(e) => set('type', e.target.value)}>
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </Select>
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Datum *</FieldLabel>
            <Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} />
          </FieldGroup>
        </Row>

        <Row>
          <FieldGroup>
            <FieldLabel>Starttijd *</FieldLabel>
            <Input type="time" value={form.startTime} onChange={(e) => set('startTime', e.target.value)} />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Eindtijd</FieldLabel>
            <Input type="time" value={form.endTime} onChange={(e) => set('endTime', e.target.value)} />
          </FieldGroup>
        </Row>

        <FieldGroup>
          <FieldLabel>Locatie</FieldLabel>
          <Input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="bv. Repetitielokaal De Kring" />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Omschrijving</FieldLabel>
          <Textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Kleur</FieldLabel>
          <ColorPickerField value={form.color} onChange={(c) => set('color', c)} />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Groep *</FieldLabel>
          <GroupSelect groups={groups} selected={form.groups} onChange={(ids) => set('groups', ids)} />
        </FieldGroup>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Annuleer</Button>
          <Button onClick={handleSave} disabled={saving || !form.title || !form.date || !form.startTime || form.groups.length === 0}>
            {saving ? 'Opslaan…' : 'Opslaan'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

// ── Template form modal ───────────────────────────────────────────────────────

const TemplateModal = ({
  initial,
  groups,
  onSave,
  onClose,
}: {
  initial: TemplateFormData & { id?: string };
  groups: OrchestraGroup[];
  onSave: (data: TemplateFormData & { id?: string }) => Promise<void>;
  onClose: () => void;
}) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const set = (key: keyof TemplateFormData, val: string | string[]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.title || !form.startTime || form.groups.length === 0) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3>{form.id ? 'Sjabloon bewerken' : 'Nieuw sjabloon'}</h3>
          <ActionBtn onClick={onClose}><X size={18} /></ActionBtn>
        </ModalHeader>

        <FieldGroup>
          <FieldLabel>Titel *</FieldLabel>
          <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="bv. Wekelijkse repetitie" />
        </FieldGroup>

        <Row>
          <FieldGroup>
            <FieldLabel>Type *</FieldLabel>
            <Select value={form.type} onChange={(e) => set('type', e.target.value)}>
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </Select>
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Dag van de week</FieldLabel>
            <Select value={form.dayOfWeek} onChange={(e) => set('dayOfWeek', e.target.value)}>
              {DAY_NAMES.map((d, i) => <option key={i} value={String(i)}>{d}</option>)}
            </Select>
          </FieldGroup>
        </Row>

        <Row>
          <FieldGroup>
            <FieldLabel>Starttijd *</FieldLabel>
            <Input type="time" value={form.startTime} onChange={(e) => set('startTime', e.target.value)} />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Eindtijd</FieldLabel>
            <Input type="time" value={form.endTime} onChange={(e) => set('endTime', e.target.value)} />
          </FieldGroup>
        </Row>

        <FieldGroup>
          <FieldLabel>Locatie</FieldLabel>
          <Input value={form.location} onChange={(e) => set('location', e.target.value)} />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Omschrijving</FieldLabel>
          <Textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Kleur</FieldLabel>
          <ColorPickerField value={form.color} onChange={(c) => set('color', c)} />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Groep *</FieldLabel>
          <GroupSelect groups={groups} selected={form.groups} onChange={(ids) => set('groups', ids)} />
        </FieldGroup>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Annuleer</Button>
          <Button onClick={handleSave} disabled={saving || !form.title || !form.startTime || form.groups.length === 0}>
            {saving ? 'Opslaan…' : 'Opslaan'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

// ── Generate modal ────────────────────────────────────────────────────────────

const GenerateModal = ({
  template,
  onGenerate,
  onClose,
}: {
  template: EventTemplate;
  onGenerate: (templateId: string, weeks: number, startDate: string) => Promise<void>;
  onClose: () => void;
}) => {
  const [weeks, setWeeks] = useState('8');
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try { await onGenerate(template.id, Number(weeks), startDate); } finally { setSaving(false); }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <ModalHeader>
          <h3>Genereer vanuit sjabloon</h3>
          <ActionBtn onClick={onClose}><X size={18} /></ActionBtn>
        </ModalHeader>

        <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '1rem' }}>
          <strong>{template.title}</strong> — elke {DAY_NAMES[template.dayOfWeek]} om {template.startTime}
        </p>

        <GenerateBox>
          <FieldGroup>
            <FieldLabel>Startdatum</FieldLabel>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </FieldGroup>
          <FieldGroup style={{ marginBottom: 0 }}>
            <FieldLabel>Aantal weken</FieldLabel>
            <Input
              type="number"
              min="1"
              max="52"
              value={weeks}
              onChange={(e) => setWeeks(e.target.value)}
            />
          </FieldGroup>
        </GenerateBox>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Annuleer</Button>
          <Button onClick={handle} disabled={saving}>
            {saving ? 'Genereren…' : `Genereer ${weeks} evenementen`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

// ── Shared event table ────────────────────────────────────────────────────────

type SortKey = 'date' | 'title' | 'type' | 'startTime' | 'endTime' | 'location' | 'group' | 'status';
type SortDir = 'asc' | 'desc';

const SortTh = styled(Th)`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover { background: #f0f4ff; }
`;

const SortIcon = styled.span`
  display: inline-flex;
  flex-direction: column;
  margin-left: 0.3rem;
  vertical-align: middle;
  line-height: 1;
  gap: 1px;

  svg { display: block; }
`;

function sortEvents(events: Event[], key: SortKey, dir: SortDir): Event[] {
  return [...events].sort((a, b) => {
    let av: string;
    let bv: string;
    switch (key) {
      case 'date':      av = a.date; bv = b.date; break;
      case 'title':     av = a.title.toLowerCase(); bv = b.title.toLowerCase(); break;
      case 'type':      av = TYPE_LABELS[a.type] ?? a.type; bv = TYPE_LABELS[b.type] ?? b.type; break;
      case 'startTime': av = a.startTime; bv = b.startTime; break;
      case 'endTime':   av = displayEndTime(a); bv = displayEndTime(b); break;
      case 'location':  av = a.location ?? ''; bv = b.location ?? ''; break;
      case 'group':     av = a.groups[0]?.name ?? ''; bv = b.groups[0]?.name ?? ''; break;
      case 'status':    av = a.status; bv = b.status; break;
    }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return dir === 'asc' ? cmp : -cmp;
  });
}

const EventTable = ({
  events,
  onAttendance,
  onEdit,
  onCancel,
  onDelete,
}: {
  events: Event[];
  onAttendance: (id: string) => void;
  onEdit: (ev: Event) => void;
  onCancel: (ev: Event) => void;
  onDelete: (id: string) => void;
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = sortEvents(events, sortKey, sortDir);

  const SortHeader = ({ col, label }: { col: SortKey; label: string }) => {
    const active = sortKey === col;
    return (
      <SortTh onClick={() => handleSort(col)}>
        {label}
        <SortIcon>
          <ChevronUp size={11} color={active && sortDir === 'asc' ? '#1e40af' : '#cbd5e1'} />
          <ChevronDown size={11} color={active && sortDir === 'desc' ? '#1e40af' : '#cbd5e1'} />
        </SortIcon>
      </SortTh>
    );
  };

  return (
    <Table>
      <thead>
        <tr>
          <SortHeader col="date" label="Datum" />
          <SortHeader col="title" label="Titel" />
          <SortHeader col="type" label="Type" />
          <SortHeader col="startTime" label="Starttijd" />
          <SortHeader col="endTime" label="Eindtijd" />
          <SortHeader col="location" label="Locatie" />
          <SortHeader col="group" label="Groepen" />
          <SortHeader col="status" label="Status" />
          <Th>Acties</Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((ev) => (
          <tr key={ev.id} style={ev.status === 'cancelled' ? { opacity: 0.5 } : {}}>
            <Td style={{ whiteSpace: 'nowrap' }}>{formatDate(ev.date)}</Td>
            <Td>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                {ev.color && (
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: ev.color, flexShrink: 0, display: 'inline-block' }} />
                )}
                {ev.title}
                {ev.template && (
                  <span style={{ fontSize: '0.75rem', color: '#888', marginLeft: '0.2rem' }}>(sjabloon)</span>
                )}
              </div>
            </Td>
            <Td><Badge $color={typeColor(ev.type)}>{TYPE_LABELS[ev.type] ?? ev.type}</Badge></Td>
            <Td>{ev.startTime}</Td>
            <Td style={{ color: '#555' }}>{displayEndTime(ev)}</Td>
            <Td style={{ color: '#555' }}>{ev.location ?? '—'}</Td>
            <Td>
              {ev.groups.length === 0
                ? <span style={{ color: '#aaa' }}>—</span>
                : ev.groups.map((g) => <Badge key={g.id} $color="purple">{g.name}</Badge>)}
            </Td>
            <Td>
              <Badge $color={ev.status === 'cancelled' ? 'red' : 'green'}>
                {ev.status === 'cancelled' ? 'Geannuleerd' : 'Actief'}
              </Badge>
            </Td>
            <Td>
              <ActionBtn onClick={() => onAttendance(ev.id)} title="Aanwezigheid">
                <Users size={16} />
              </ActionBtn>
              <ActionBtn onClick={() => onEdit(ev)} title="Bewerken">
                <Pencil size={16} />
              </ActionBtn>
              <ActionBtn onClick={() => onCancel(ev)} title={ev.status === 'cancelled' ? 'Heractiveren' : 'Annuleren'}>
                <X size={16} />
              </ActionBtn>
              <DeleteBtn onClick={() => onDelete(ev.id)} title="Verwijderen">
                <Trash2 size={16} />
              </DeleteBtn>
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export const EventsTab = () => {
  const [subTab, setSubTab] = useState<'events' | 'templates'>('events');
  const [events, setEvents] = useState<Event[]>([]);
  const [templates, setTemplates] = useState<EventTemplate[]>([]);
  const [groups, setGroups] = useState<OrchestraGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [upcomingCollapsed, setUpcomingCollapsed] = useState(false);
  const [pastCollapsed, setPastCollapsed] = useState(false);

  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EventTemplate | null>(null);
  const [generatingTemplate, setGeneratingTemplate] = useState<EventTemplate | null>(null);
  const [attendanceEventId, setAttendanceEventId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmOptions | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const showConfirm = useCallback((opts: Omit<ConfirmOptions, 'onCancel'> & { onCancel?: () => void }) => {
    setConfirm({ onCancel: () => setConfirm(null), ...opts });
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, tmpl, grp] = await Promise.all([
        client.get('/events'),
        client.get('/events/templates'),
        client.get('/members/groups'),
      ]);
      setEvents(ev.data);
      setTemplates(tmpl.data);
      setGroups(grp.data);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Ophalen kalender mislukt'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Search / filter / split ──

  const cutoff = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 3);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const matchesFilters = useCallback((ev: Event) => {
    if (filterType && ev.type !== filterType) return false;
    if (filterGroup && !ev.groups.some((g) => g.id === filterGroup)) return false;
    if (filterStatus && ev.status !== filterStatus) return false;
    if (search) {
      const q = search.trim().toLowerCase();
      const haystack = [
        ev.title,
        ev.date.slice(0, 10),
        formatDate(ev.date),
        ev.startTime,
        ev.endTime ?? '',
        ev.location ?? '',
      ].join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  }, [search, filterType, filterGroup, filterStatus]);

  const upcomingEvents = useMemo(
    () => events.filter((ev) => new Date(ev.date) >= cutoff && matchesFilters(ev)),
    [events, cutoff, matchesFilters],
  );

  const pastEvents = useMemo(
    () => events.filter((ev) => new Date(ev.date) < cutoff && matchesFilters(ev)),
    [events, cutoff, matchesFilters],
  );

  // ── Event CRUD ──

  const handleSaveEvent = async (form: EventFormData & { id?: string }) => {
    try {
      if (form.id) {
        await client.patch(`/events/${form.id}`, { ...form, groups: form.groups });
      } else {
        await client.post('/events', { ...form, groups: form.groups });
      }
      setShowEventModal(false);
      setEditingEvent(null);
      fetchAll();
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Opslaan mislukt'), 'error');
    }
  };

  const handleCancelEvent = (event: Event) => {
    const next = event.status === 'cancelled' ? 'active' : 'cancelled';
    showConfirm({
      title: next === 'cancelled' ? 'Evenement annuleren' : 'Evenement heractiveren',
      message: next === 'cancelled'
        ? 'Leden zien dit evenement als geannuleerd.'
        : 'Het evenement wordt opnieuw actief.',
      confirmLabel: next === 'cancelled' ? 'Annuleren' : 'Heractiveren',
      variant: next === 'cancelled' ? 'danger' : 'info',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await client.patch(`/events/${event.id}`, { status: next });
          fetchAll();
        } catch (error) {
          showToast(getApiErrorMessage(error, 'Status wijzigen mislukt'), 'error');
        }
      },
    });
  };

  const handleDeleteEvent = (id: string) => {
    showConfirm({
      title: 'Evenement verwijderen',
      message: 'Het evenement wordt definitief verwijderd. Dit kan niet ongedaan worden gemaakt.',
      confirmLabel: 'Verwijderen',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await client.delete(`/events/${id}`);
          fetchAll();
        } catch (error) {
          showToast(getApiErrorMessage(error, 'Verwijderen mislukt'), 'error');
        }
      },
    });
  };

  // ── Template CRUD ──

  const handleSaveTemplate = async (form: TemplateFormData & { id?: string }) => {
    try {
      const payload = { ...form, dayOfWeek: Number(form.dayOfWeek), groups: form.groups };
      if (form.id) {
        await client.patch(`/events/templates/${form.id}`, payload);
      } else {
        await client.post('/events/templates', payload);
      }
      setShowTemplateModal(false);
      setEditingTemplate(null);
      fetchAll();
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Opslaan mislukt'), 'error');
    }
  };

  const handleDeleteTemplate = (id: string) => {
    showConfirm({
      title: 'Sjabloon verwijderen',
      message: 'Eerder gegenereerde evenementen blijven bestaan. Alleen het sjabloon wordt verwijderd.',
      confirmLabel: 'Verwijderen',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await client.delete(`/events/templates/${id}`);
          fetchAll();
        } catch (error) {
          showToast(getApiErrorMessage(error, 'Verwijderen mislukt'), 'error');
        }
      },
    });
  };

  const handleGenerate = async (templateId: string, weeks: number, startDate: string) => {
    try {
      await client.post(`/events/templates/${templateId}/generate`, { weeks, startDate });
      setGeneratingTemplate(null);
      fetchAll();
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Genereren mislukt'), 'error');
    }
  };

  // ── Derive modal initial state ──

  const eventFormInitial: EventFormData & { id?: string } = editingEvent
    ? {
        id: editingEvent.id,
        title: editingEvent.title,
        type: editingEvent.type,
        date: toDateInputValue(editingEvent.date),
        startTime: editingEvent.startTime,
        endTime: editingEvent.endTime ?? endTimeFromDescription(editingEvent.description, editingEvent.startTime) ?? '',
        location: editingEvent.location ?? '',
        description: editingEvent.description ?? '',
        color: editingEvent.color ?? '',
        groups: editingEvent.groups.map((g) => g.id),
      }
    : { ...EMPTY_EVENT_FORM };

  const templateFormInitial: TemplateFormData & { id?: string } = editingTemplate
    ? {
        id: editingTemplate.id,
        title: editingTemplate.title,
        type: editingTemplate.type,
        dayOfWeek: String(editingTemplate.dayOfWeek),
        startTime: editingTemplate.startTime,
        endTime: editingTemplate.endTime ?? '',
        location: editingTemplate.location ?? '',
        description: editingTemplate.description ?? '',
        color: editingTemplate.color ?? '',
        groups: editingTemplate.groups.map((g) => g.id),
      }
    : { ...EMPTY_TEMPLATE_FORM };

  if (attendanceEventId) {
    return (
      <AttendanceDetail
        eventId={attendanceEventId}
        onBack={() => setAttendanceEventId(null)}
      />
    );
  }

  return (
    <>
      <SubTabs>
        <SubTab $active={subTab === 'events'} onClick={() => setSubTab('events')}>
          Kalender
        </SubTab>
        <SubTab $active={subTab === 'templates'} onClick={() => setSubTab('templates')}>
          Sjablonen
        </SubTab>
      </SubTabs>

      {/* ── Events list ─────────────────────────────────────────────────── */}
      {subTab === 'events' && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2>Kalender</h2>
            <Button onClick={() => { setEditingEvent(null); setShowEventModal(true); }}>
              <Plus size={16} style={{ marginRight: '0.5rem' }} /> Nieuw evenement
            </Button>
          </div>

          <Toolbar>
            <SearchLabel>
              <Search size={14} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Zoek op titel, datum, tijd of locatie…"
              />
            </SearchLabel>
            <FilterSelect value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="">Alle types</option>
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </FilterSelect>
            <FilterSelect value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
              <option value="">Alle groepen</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </FilterSelect>
            <FilterSelect value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">Alle statussen</option>
              <option value="active">Actief</option>
              <option value="cancelled">Geannuleerd</option>
            </FilterSelect>
          </Toolbar>

          {loading ? (
            <p>Laden…</p>
          ) : events.length === 0 ? (
            <p style={{ color: '#888' }}>Nog geen evenementen in de kalender. Maak er een aan of genereer vanuit een sjabloon.</p>
          ) : (
            <>
              {/* Upcoming */}
              <SectionTitle onClick={() => setUpcomingCollapsed((v) => !v)}>
                {upcomingCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                Aankomende evenementen ({upcomingEvents.length})
              </SectionTitle>
              {!upcomingCollapsed && (
                upcomingEvents.length === 0 ? (
                  <p style={{ color: '#888', fontSize: '0.9rem' }}>Geen aankomende evenementen gevonden.</p>
                ) : (
                  <EventTable
                    events={upcomingEvents}
                    onAttendance={setAttendanceEventId}
                    onEdit={(ev) => { setEditingEvent(ev); setShowEventModal(true); }}
                    onCancel={handleCancelEvent}
                    onDelete={handleDeleteEvent}
                  />
                )
              )}

              {/* Past */}
              {pastEvents.length > 0 && (
                <>
                  <SectionTitle onClick={() => setPastCollapsed((v) => !v)}>
                    {pastCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                    Voorbije evenementen ({pastEvents.length})
                  </SectionTitle>
                  {!pastCollapsed && (
                    <EventTable
                      events={pastEvents}
                      onAttendance={setAttendanceEventId}
                      onEdit={(ev) => { setEditingEvent(ev); setShowEventModal(true); }}
                      onCancel={handleCancelEvent}
                      onDelete={handleDeleteEvent}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Card>
      )}

      {/* ── Templates list ───────────────────────────────────────────────── */}
      {subTab === 'templates' && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2>Sjablonen</h2>
            <Button onClick={() => { setEditingTemplate(null); setShowTemplateModal(true); }}>
              <Plus size={16} style={{ marginRight: '0.5rem' }} /> Nieuw sjabloon
            </Button>
          </div>
          <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '1rem' }}>
            Sjablonen beschrijven een terugkerend evenement. Gebruik "Genereer" om meerdere exemplaren aan te maken.
          </p>

          {loading ? (
            <p>Laden…</p>
          ) : templates.length === 0 ? (
            <p style={{ color: '#888' }}>Nog geen sjablonen.</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Titel</Th>
                  <Th>Type</Th>
                  <Th>Dag</Th>
                  <Th>Starttijd</Th>
                  <Th>Eindtijd</Th>
                  <Th>Locatie</Th>
                  <Th>Groepen</Th>
                  <Th>Acties</Th>
                </tr>
              </thead>
              <tbody>
                {templates.map((tmpl) => (
                  <tr key={tmpl.id}>
                    <Td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        {tmpl.color && (
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: tmpl.color, flexShrink: 0, display: 'inline-block' }} />
                        )}
                        {tmpl.title}
                      </div>
                    </Td>
                    <Td><Badge $color={typeColor(tmpl.type)}>{TYPE_LABELS[tmpl.type] ?? tmpl.type}</Badge></Td>
                    <Td>{DAY_NAMES[tmpl.dayOfWeek]}</Td>
                    <Td>{tmpl.startTime}</Td>
                    <Td style={{ color: '#555' }}>{tmpl.endTime ?? '—'}</Td>
                    <Td style={{ color: '#555' }}>{tmpl.location ?? '—'}</Td>
                    <Td>
                      {tmpl.groups.length === 0
                        ? <span style={{ color: '#aaa' }}>—</span>
                        : tmpl.groups.map((g) => <Badge key={g.id} $color="purple">{g.name}</Badge>)}
                    </Td>
                    <Td>
                      <ActionBtn onClick={() => setGeneratingTemplate(tmpl)} title="Genereer evenementen">
                        <Calendar size={16} />
                      </ActionBtn>
                      <ActionBtn onClick={() => { setEditingTemplate(tmpl); setShowTemplateModal(true); }} title="Bewerken">
                        <Pencil size={16} />
                      </ActionBtn>
                      <DeleteBtn onClick={() => handleDeleteTemplate(tmpl.id)} title="Verwijderen">
                        <Trash2 size={16} />
                      </DeleteBtn>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      {showEventModal && (
        <EventModal
          initial={eventFormInitial}
          groups={groups}
          onSave={handleSaveEvent}
          onClose={() => { setShowEventModal(false); setEditingEvent(null); }}
        />
      )}

      {showTemplateModal && (
        <TemplateModal
          initial={templateFormInitial}
          groups={groups}
          onSave={handleSaveTemplate}
          onClose={() => { setShowTemplateModal(false); setEditingTemplate(null); }}
        />
      )}

      {generatingTemplate && (
        <GenerateModal
          template={generatingTemplate}
          onGenerate={handleGenerate}
          onClose={() => setGeneratingTemplate(null)}
        />
      )}

      {confirm && <ConfirmDialog {...confirm} />}
      {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
    </>
  );
};
